import React from "react";
import {
  Sparkles, BellRing, ShieldCheck, Check, AlertTriangle,
} from "lucide-react";
import { useT } from "../lib/i18n";
import { cn } from "../lib/utils";

const MATCH_MOCKUP = "https://customer-assets-jt897jd0.emergentagent.net/job_ad0cee43-adb9-42e2-b070-e52d3c5da298/artifacts/8nzrii1r_Minimalistische%20WoningAlert-appmock-up.png";

// ---- Phone frame ----
const PhoneMock = ({ children, className }) => (
  <div className={cn("relative mx-auto w-[248px]", className)}>
    <div className="relative rounded-[2.6rem] border-[7px] border-navy bg-navy shadow-2xl">
      <div className="absolute left-1/2 top-2 z-10 h-5 w-24 -translate-x-1/2 rounded-full bg-navy" />
      <div className="h-[496px] overflow-hidden rounded-[2rem] bg-slate-50">{children}</div>
    </div>
    {/* glow */}
    <div className="absolute -inset-6 -z-10 rounded-full bg-brandblue/20 blur-3xl" />
  </div>
);

const StatusBar = () => (
  <div className="flex items-center justify-between px-5 pt-3 text-[10px] font-semibold text-navy/80">
    <span>9:41</span>
    <span>WoningAlert</span>
  </div>
);

// ---- Screen 1: Match score (uses full phone mockup image) ----
const MatchMockup = () => (
  <div className="relative mx-auto w-full max-w-[320px]">
    <img
      src={MATCH_MOCKUP}
      alt="Slimme matchscore in de WoningAlert-app"
      className="relative z-10 w-full h-auto"
      loading="lazy"
    />
    <div className="absolute -inset-6 -z-10 rounded-full bg-brandblue/20 blur-3xl" />
  </div>
);

// ---- Screen 2: Alerts ----
const AlertScreen = () => (
  <div>
    <StatusBar />
    <div className="space-y-3 p-4">
      <div className="text-sm font-extrabold text-navy">Nieuwe meldingen</div>
      {[
        { city: "Amsterdam", meta: "2-kamer · €1.250 · 96% match", t: "nu" },
        { city: "Utrecht", meta: "Studio · €995 · 88% match", t: "3 min" },
        { city: "Den Haag", meta: "Appartement · €1.400 · 82% match", t: "12 min" },
      ].map((n, i) => (
        <div key={i} className="flex items-start gap-3 rounded-2xl bg-white p-3 shadow-sm ring-1 ring-slate-100">
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-brandblue-light text-brandblue">
            <BellRing size={15} />
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex items-center justify-between">
              <div className="text-[12px] font-bold text-navy">Nieuwe woning in {n.city}</div>
              <div className="text-[9px] text-slate-400">{n.t}</div>
            </div>
            <div className="truncate text-[11px] text-slate-500">{n.meta}</div>
          </div>
        </div>
      ))}
      <div className="rounded-2xl bg-navy p-3 text-center text-[11px] font-semibold text-white">
        Reageer als eerste — vaak binnen enkele minuten
      </div>
    </div>
  </div>
);

// ---- Screen 3: Scam check ----
const ScamScreen = () => (
  <div>
    <StatusBar />
    <div className="p-4">
      <div className="rounded-2xl bg-white p-4 text-center shadow-sm ring-1 ring-slate-100">
        <div className="relative mx-auto h-24 w-24">
          <svg viewBox="0 0 120 120" className="h-full w-full -rotate-90">
            <circle cx="60" cy="60" r="50" fill="none" stroke="#FEE2E2" strokeWidth="12" />
            <circle cx="60" cy="60" r="50" fill="none" stroke="#EF4444" strokeWidth="12" strokeLinecap="round" strokeDasharray="314" strokeDashoffset="278" />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className="text-2xl font-extrabold text-navy">12</span>
            <span className="text-[9px] text-slate-400">/ 100</span>
          </div>
        </div>
        <span className="mt-2 inline-flex items-center gap-1 rounded-full bg-danger/15 px-2.5 py-0.5 text-[11px] font-bold text-danger">
          <AlertTriangle size={11} /> Hoog risico
        </span>
        <div className="mt-3 space-y-1.5 text-left">
          {["Huurprijs verdacht laag", "Borg vooraf via Western Union", "Geen bezichtiging mogelijk"].map((t) => (
            <div key={t} className="flex items-start gap-1.5 text-[11px] text-slate-700">
              <span className="mt-1 h-1 w-1 shrink-0 rounded-full bg-danger" /> {t}
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

export const FeatureShowcase = () => {
  const { t } = useT();
  const features = [
    { icon: Sparkles, title: t("home.usp1.title"), desc: t("home.usp1.desc"), screen: <MatchMockup />, raw: true },
    { icon: BellRing, title: t("home.usp2.title"), desc: t("home.usp2.desc"), screen: <AlertScreen /> },
    { icon: ShieldCheck, title: t("home.usp3.title"), desc: t("home.usp3.desc"), screen: <ScamScreen /> },
  ];

  return (
    <section className="mx-auto max-w-6xl px-4 py-20 sm:px-6">
      <div className="mx-auto max-w-xl text-center">
        <span className="inline-flex items-center gap-2 rounded-full bg-brandblue-light px-3 py-1 text-sm font-semibold text-brandblue">
          <Sparkles size={14} /> {t("showcase.tag")}
        </span>
        <h2 className="mt-4 font-display text-3xl font-extrabold text-navy sm:text-4xl">{t("showcase.title")}</h2>
      </div>

      <div className="mt-14 space-y-20">
        {features.map((f, i) => (
          <div key={i} className={cn("grid items-center gap-10 lg:grid-cols-2", i % 2 === 1 && "lg:[&>*:first-child]:order-2")}>
            <div className={cn(i % 2 === 1 && "lg:pl-12")}>
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-brandblue text-white">
                <f.icon size={24} />
              </div>
              <h3 className="mt-5 font-display text-2xl font-extrabold text-navy">{f.title}</h3>
              <p className="mt-3 text-slate-500">{f.desc}</p>
              <div className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-500">
                <Check size={13} className="text-success" /> {t("showcase.inapp")}
              </div>
            </div>
            {f.raw ? f.screen : <PhoneMock>{f.screen}</PhoneMock>}
          </div>
        ))}
      </div>
    </section>
  );
};
